// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, cloneElement, Component } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';

import './Grid.less';

/**
 * `Grid` component.
 */
class Grid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            xs: this.props.xs,
        };
    }

    handleResize = ({ entry: { width } }) => {
        let { maxColumnsNumber } = this.props;

        if (typeof maxColumnsNumber === 'function') {
            maxColumnsNumber = maxColumnsNumber({ width });
        }

        const xs = Math.max(Math.floor((width + this.getGapSize()) / (this.props.minColumnWidth + this.getGapSize())), 1);
        this.setState({ xs: Math.min(xs, maxColumnsNumber) });
        if (this.props.onColumnsChange) {
            this.props.onColumnsChange(xs);
        }
    };

    getGapSize() {
        switch (this.props.gap) {
            case 'xs':
                return 8;
            case 'sm':
                return 12;
            case 'md':
                return 16;
            case 'lg':
                return 24;
            case 'xl':
                return 32;
            default:
                return 0;
        }
    }

    render() {
        const {
            baseClassName,
            className,
            minColumnWidth,
            onColumnsChange,
            maxColumnsNumber,
            xs,
            sm,
            md,
            lg,
            xl,
            gap,
            children,
            ...props
        } = this.props;


        const computedClassName = classNames(
            baseClassName,
            {
                [`${baseClassName}--xs-${this.state.xs}`]: this.state.xs,
                [`${baseClassName}--sm-${sm}`]: sm,
                [`${baseClassName}--md-${md}`]: md,
                [`${baseClassName}--lg-${lg}`]: lg,
                [`${baseClassName}--xl-${xl}`]: xl,
                [`${baseClassName}--gap-xs`]: gap === 'xs',
                [`${baseClassName}--gap-sm`]: gap === 'sm',
                [`${baseClassName}--gap-md`]: gap === 'md',
                [`${baseClassName}--gap-lg`]: gap === 'lg',
                [`${baseClassName}--gap-xl`]: gap === 'xl',
            },
            className
        );

        const result = (
            <div className={computedClassName} {...props}>
                {minColumnWidth > 0 && !this.state.xs ? null : children}
            </div>
        );

        if (minColumnWidth > 0) {
            return (
                <Measure onResize={this.handleResize}>
                    {({ measureRef }) => cloneElement(result, { ref: measureRef })}
                </Measure>
            );
        }

        return result;
    }
}

Grid.propTypes = {
    /**
     * Content of the `Grid`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
    /**
     * Minimal column width
     */
    minColumnWidth: PropTypes.number,
    /**
     * Maximum columns number
     */
    maxColumnsNumber: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.func,
    ]),
    /**
     * Number of columns for `xs` breakpoint
     */
    xs: PropTypes.PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),
    /**
     * Number of columns for `sm` breakpoint
     */
    sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),
    /**
     * Number of columns for `md` breakpoint
     */
    md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),
    /**
     * Number of columns for `lg` breakpoint
     */
    lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),
    /**
     * Number of columns for `xl` breakpoint
     */
    xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),
    /**
     * Specifying the gutters between grid items
     */
    gap: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    /**
     * Columns number change notifier
     */
    onColumnsChange: PropTypes.func,
};
Grid.defaultProps = {
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}grid`,
    minColumnWidth: null,
    maxColumnsNumber: 6,
    xs: null,
    sm: null,
    md: null,
    lg: null,
    xl: null,
    gap: null,
    onColumnsChange: null,
};

export default Grid;




// WEBPACK FOOTER //
// ./components/Grid/Grid.js